import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, FolderOpen, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { timeAgo, duration } from '../lib/format';
import { ActivityList } from './ActivityList';

export default function Dashboard() {
  const qc = useQueryClient();
  const projects = useQuery({ queryKey: ['bootstrap'], queryFn: api.bootstrap });
  const feed = useQuery({
    queryKey: ['feed'],
    queryFn: api.feed,
    refetchInterval: 30_000,
  });
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');

  const createProject = useMutation({
    mutationFn: () => api.createProject({ name }),
    onSuccess: () => {
      setName('');
      setCreating(false);
      void qc.invalidateQueries({ queryKey: ['bootstrap'] });
    },
    onError: (e) => toast.error(e.message),
  });

  const totalVisits30d =
    feed.data?.visitsPerDay.reduce((a, d) => a + d.count, 0) ?? 0;

  return (
    <div className="space-y-10">
      {/* ── Visit stats ── */}
      <section className="grid gap-6 lg:grid-cols-[1fr_360px]">
        <div className="rw-card p-5">
          <div className="mb-4 flex items-baseline justify-between">
            <h2 className="rw-heading text-xs text-mid">Visits — last 30 days</h2>
            <span className="font-heading text-2xl font-semibold text-ink">
              {totalVisits30d}
            </span>
          </div>
          <VisitStatsChart data={feed.data?.visitsPerDay ?? []} />
        </div>

        <div className="rw-card p-5">
          <h2 className="rw-heading mb-4 text-xs text-mid">
            Most engaged contacts
          </h2>
          <div className="space-y-2.5">
            {feed.data?.topContacts.map((c, i) => (
              <div key={c.email} className="flex items-center gap-2.5">
                <span className="w-4 text-right font-heading text-xs font-semibold text-soft">
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-ink">
                    {c.email}
                  </div>
                  <div className="text-[11px] text-mid">
                    {duration(c.totalSeconds)} · {c.visits} visit
                    {c.visits === 1 ? '' : 's'} · {c.docs} doc
                    {c.docs === 1 ? '' : 's'} · {timeAgo(c.lastSeen)}
                  </div>
                </div>
                <button
                  title="Copy email"
                  onClick={() => {
                    void navigator.clipboard.writeText(c.email);
                    toast.success('Copied');
                  }}
                  className="p-1 text-soft hover:text-accent"
                >
                  <Mail className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            {feed.data && feed.data.topContacts.length === 0 && (
              <p className="text-sm text-mid">
                Engagement rankings appear once identified visitors spend time on
                your documents.
              </p>
            )}
          </div>
        </div>
      </section>

      {/* ── Projects ── */}
      <section>
        <div className="mb-4 flex items-center justify-between">
          <h1 className="rw-heading text-sm text-white">Projects</h1>
          <button onClick={() => setCreating(true)} className="rw-btn">
            <Plus className="h-3.5 w-3.5" /> New project
          </button>
        </div>

        {creating && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              createProject.mutate();
            }}
            className="rw-card mb-4 flex gap-2 p-3"
          >
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Project name, e.g. Fund II Raise"
              className="flex-1 border border-line bg-white px-3 py-2 text-sm text-ink focus:border-accent focus:outline-none"
            />
            <button
              disabled={!name.trim() || createProject.isPending}
              className="rw-btn"
            >
              Create
            </button>
            <button
              type="button"
              onClick={() => setCreating(false)}
              className="px-3 text-xs text-mid hover:text-ink"
            >
              Cancel
            </button>
          </form>
        )}

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {projects.data?.projects.map((p) => (
            <Link
              key={p.projectId}
              to={`/p/${p.projectId}`}
              className="rw-card group p-4 transition-transform hover:-translate-y-0.5"
            >
              <div className="flex items-center gap-2">
                <FolderOpen className="h-4 w-4 text-accent" />
                <span className="font-heading text-sm font-semibold text-ink">
                  {p.name}
                </span>
              </div>
              <div className="mt-2 text-xs text-mid">
                {p.docCount ?? 0} docs · {p.linkCount ?? 0} links
                {p.ndaDocId && ' · NDA set'}
              </div>
            </Link>
          ))}
          {projects.data && projects.data.projects.length === 0 && !creating && (
            <p className="col-span-full border border-dashed border-white/20 p-8 text-center text-sm text-soft">
              Create a project, upload a deck, and share tracked links with
              investors.
            </p>
          )}
        </div>
      </section>

      {/* ── Activity ── */}
      <section>
        <h2 className="rw-heading mb-4 text-sm text-white">Recent activity</h2>
        <ActivityList
          visits={feed.data?.visits ?? []}
          raw={feed.data?.raw ?? []}
          docTitles={feed.data?.docTitles ?? {}}
          showLink
          showProject
          emptyText="Visits will appear here the moment someone opens a link."
        />
      </section>
    </div>
  );
}

function VisitStatsChart({ data }: { data: { date: string; count: number }[] }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div>
      <div className="flex items-end gap-[3px]" style={{ height: 110 }}>
        {data.map((d) => (
          <div
            key={d.date}
            title={`${d.date}: ${d.count} visit${d.count === 1 ? '' : 's'}`}
            className="flex-1"
            style={{
              height: `${Math.max(3, (d.count / max) * 100)}%`,
              backgroundColor: d.count > 0 ? '#8b1a10' : '#e8e8e5',
              opacity: d.count > 0 ? 0.5 + 0.5 * (d.count / max) : 1,
            }}
          />
        ))}
      </div>
      <div className="mt-1.5 flex justify-between text-[10px] text-soft">
        <span>{data[0]?.date.slice(5)}</span>
        <span>today</span>
      </div>
    </div>
  );
}
